import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import useSubjectAPI from "./useSubjectAPI";
import { alcohol_use_frequencies, smoker_statuses } from "../enums";
import { ENUM_MAPS, normalizeEnum } from "./enumMaps";

const HealthInformationSection = ({ subjectId, form }) => {
  const isCreate = !subjectId;
  const [open, setOpen] = useState(true);

  const purple = "#2E2868";
  const pillBg = "#ebeaf3";

  const { loadSubject, saveSubjectDirect } = useSubjectAPI(subjectId);

  const [values, setValues] = useState({
    bmi: "",
    height_in: "",
    weight_lb: "",
    alcohol_use_frequency: "",
    smoker_status: "",
  });

  
  const [originalValues, setOriginalValues] = useState({});

  const [editing, setEditing] = useState({
    height_in: false,
    weight_lb: false,
    alcohol_use_frequency: false,
    smoker_status: false,
  });

  /* LOAD (EDIT MODE ONLY) */
  useEffect(() => {
    if (isCreate) return;

    loadSubject().then((res) => {
      if (!res) return;
      setValues({
        bmi: res.bmi || "",
        height_in: res.height_in || "",
        weight_lb: res.weight_lb || "",
        alcohol_use_frequency: res.alcohol_use_frequency || "",
        smoker_status: res.smoker_status || "",
      });
    });
  }, [isCreate, subjectId]);

  /* BMI Calculation Logic */
  useEffect(() => {
    if (!values.height_in || !values.weight_lb) return;

    const h = parseFloat(values.height_in);
    const w = parseFloat(values.weight_lb);

    if (h > 0 && w > 0) {
      const bmi = ((w / (h * h)) * 703).toFixed(1);
      setValues((prev) => ({ ...prev, bmi }));
    }
  }, [values.height_in, values.weight_lb]);

  /* CREATE MODE → FORM MODEL */
  useEffect(() => {
    if (!isCreate || !form?.setValue) return;

    form.setValue({
      key: "subjects",
      value: {
        ...(form.model?.subjects || {}),
        height_in: values.height_in || null,
        weight_lb: values.weight_lb || null,
        bmi: values.bmi || null,
        alcohol_use_frequency: values.alcohol_use_frequency || null,
        smoker_status: values.smoker_status || null,
      },
    });
  }, [
    values.height_in,
    values.weight_lb,
    values.bmi,
    values.alcohol_use_frequency,
    values.smoker_status,
  ]);

  const handleChange = (field, val) => {
    setValues((prev) => ({ ...prev, [field]: val }));
  };

  const toggleEdit = (field) => {
    if (isCreate) return;
    setOriginalValues((prev) => ({ 
        ...prev, 
        [field]: values[field],
        bmi: values.bmi 
    }));
    setEditing((prev) => ({ ...prev, [field]: true }));
  };

  const handleCancel = (field) => {
    setValues((prev) => ({ 
        ...prev, 
        [field]: originalValues[field],
        bmi: originalValues.bmi 
    }));
    setEditing((prev) => ({ ...prev, [field]: false }));
  };

const saveField = async (field) => {
    if (isCreate) return;

    setEditing((prev) => ({ ...prev, [field]: false }));

    let value = values[field];

   
    if (ENUM_MAPS[field]) {
      value = normalizeEnum(values[field], ENUM_MAPS[field]);
    }

    const payload = {
      subjects: {
        id: subjectId,
        [field]: value,
      },
    };

    try {
      const res = await saveSubjectDirect(payload);
      
      if (!res) {
        throw new Error("API returned failure");
      }
    } catch (error) {
      alert("Save failed. Reverting to original value.");
      
    
      handleCancel(field);
    }
  };

  const renderInput = (label, field) => {
    const isEditing = editing[field];
    const viewStyle = {
      backgroundColor: pillBg,
      padding: "0.5rem 70px 0.5rem 0.5rem",
      cursor: "text",
      userSelect: "text",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };

    const inputStyle = {
      backgroundColor: isEditing ? "#fff" : pillBg,
      border: isEditing ? `2px solid ${purple}` : "0",
      boxShadow: isEditing ? "0 0 5px rgba(46, 40, 104, 0.2)" : "none",
      padding: isEditing
        ? "0.5rem 105px 0.5rem 0.5rem" 
        : "0.5rem 2rem 0.5rem 0.5rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: isCreate ? "pointer" : "text",
    };

    return (
      <div className="mb-3">
        {label && <label className="form-label mb-1">{label}</label>}
        <div style={{ position: "relative" }}>
          {isCreate && (
            <input
              type="number"
              value={values[field]}
              onChange={(e) => handleChange(field, e.target.value)}
              className="form-control rounded-pill"
              style={inputStyle}
              title={values[field]}
            />
          )}

          {!isCreate && !editing[field] && (
            <>
              <input
                disabled
                value={values[field]}
                className="form-control rounded-pill border-0"
                style={viewStyle}
                title={values[field]}
              />
              <FontAwesomeIcon
                icon={faPen}
                onClick={() => toggleEdit(field)}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: purple,
                  zIndex: 2,
                  fontSize: 14,
                }}
              />
            </>
          )}

          {!isCreate && editing[field] && (
            <>
              <input
                type="number"
                value={values[field]}
                onChange={(e) => handleChange(field, e.target.value)}
                className="form-control rounded-pill"
                style={inputStyle}
              />
              <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", zIndex: 2, display: "flex", gap: "4px" }}>
                <button
                  onClick={() => saveField(field)}
                  className="btn btn-sm"
                  style={{ background: purple, color: "#fff", borderRadius: 8, padding: "2px 8px", fontSize: "11px" }}
                >Save</button>
                <button
                  onClick={() => handleCancel(field)}
                  className="btn btn-sm btn-light"
                  style={{ borderRadius: 8, padding: "2px 8px", fontSize: "11px", border: "1px solid #ccc" }}
                >Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderSelect = (label, field, options) => {
    const isEditing = editing[field];
    const viewStyle = {
      backgroundColor: pillBg,
      padding: "0.5rem 70px 0.5rem 0.5rem",
      cursor: "text",
      userSelect: "text",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };

    const inputStyle = {
      backgroundColor: isEditing ? "#fff" : pillBg,
      border: isEditing ? `2px solid ${purple}` : "0",
      boxShadow: isEditing ? "0 0 5px rgba(46, 40, 104, 0.2)" : "none",
      padding: isEditing
        ? "0.5rem 105px 0.5rem 0.5rem"
        : "0.5rem 2rem 0.5rem 0.5rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: isCreate ? "pointer" : "text",
    };

    const selectedOption = options.find((o) => o.id === values[field]);
    const displayValue = selectedOption ? selectedOption.label || selectedOption.id : "";

    return (
      <div className="mb-3">
        <label className="form-label mb-1">{label}</label>
        <div style={{ position: "relative" }}>
          {isCreate && (
            <select
              value={values[field]}
              onChange={(e) => handleChange(field, e.target.value)}
              className="form-select rounded-pill"
              style={inputStyle}
              title={displayValue}
            >
              <option value="">Select...</option>
              {options.map((o, i) => <option key={i} value={o.id}>{o.label || o.id}</option>)}
            </select>
          )}

          {!isCreate && !editing[field] && (
            <>
              <div className="form-control rounded-pill border-0" style={viewStyle} title={displayValue}>
                {displayValue || "Select..."}
              </div>
              <FontAwesomeIcon
                icon={faPen}
                onClick={() => toggleEdit(field)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: purple, zIndex: 2, fontSize: 14 }}
              />
            </>
          )}

          {!isCreate && editing[field] && (
            <>
              <select
                value={values[field]}
                onChange={(e) => handleChange(field, e.target.value)}
                className="form-select rounded-pill"
                style={inputStyle}
              >
                <option value="">Select...</option>
                {options.map((o, i) => <option key={i} value={o.id}>{o.label || o.id}</option>)}
              </select>
              <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", zIndex: 2, display: "flex", gap: "4px" }}>
                <button
                  onClick={() => saveField(field)}
                  className="btn btn-sm"
                  style={{ background: purple, color: "#fff", borderRadius: 8, padding: "2px 8px", fontSize: "11px" }}
                >Save</button>
                <button
                  onClick={() => handleCancel(field)}
                  className="btn btn-sm btn-light"
                  style={{ borderRadius: 8, padding: "2px 8px", fontSize: "11px", border: "1px solid #ccc" }}
                >Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="my-3" style={{ maxWidth: 490 }}>
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3" style={{ userSelect: "none" }}>
          <div className="d-flex align-items-center">
            <span style={{ fontSize: "30px", color: "#b2a9ff", marginRight: "6px" }}>⋮⋮</span>
            <h5 className="fw-bold m-0" style={{ color: purple, cursor: "pointer" }} onClick={() => setOpen((prev) => !prev)}>
              Health Information
            </h5>
          </div>
          <div
            style={{ width: "28px", height: "28px", borderRadius: "8px", backgroundColor: purple, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "transform 0.3s ease", transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
            onClick={() => setOpen((prev) => !prev)}
          ><i className="bi bi-chevron-right" style={{ fontSize: "14px" }} /></div>
        </div>

        {open && (
          <>
            <div className="row g-2 mb-3">
              <div className="col-4">
                <label className="form-label mb-1">BMI</label>
                <input
                  disabled
                  value={values.bmi}
                  className="form-control rounded-pill border-0"
                  style={{ backgroundColor: pillBg, padding: "0.5rem 0.5rem", cursor: "text", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  title={values.bmi}
                />
              </div>
              <div className="col-4">{renderInput("Height (in)", "height_in")}</div>
              <div className="col-4">{renderInput("Weight (lb)", "weight_lb")}</div>
            </div>
            {renderSelect("Alcohol Use Frequency", "alcohol_use_frequency", alcohol_use_frequencies)}
            {renderSelect("Smoker Status", "smoker_status", smoker_statuses)}
          </>
        )}
      </div>
    </div>
  );
};

export default HealthInformationSection;